import { ClickAwayListener, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import LanguageContext from "components/LanguageContext";
import MessageContext from "components/MessageContext";
import Link from "components/templatesComponents/Link";
import { suggestionStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import scrollIntoView from "scroll-into-view-if-needed";
import PageService from "services/PageService";
import { debounce, eventKey } from "utils/commonUtils";
import t from "utils/locales/translation.json";
import { createQueryParams } from "utils/urlUtils";

const listboxId = "search-input-results";

const containerStyled = {
  width: "100%",
  position: "relative",
  p: 0,
  height: 40,
};

const suggestionsListStyled = (theme, isMobile = true) => ({
  position: "absolute",
  left: isMobile ? 12 : "auto",
  right: isMobile ? 16 : 0,
  top: 40,
  width: isMobile ? "auto" : "22.25rem",
  textAlign: "initial",
  bgcolor: theme.palette.common.white,
  border: `1px solid ${theme.palette.componentColors[30]}`,
  borderTop: "none",
  zIndex: 100,
  listStyleType: "none",
  pl: "0 !important",
  m: 0,
});

const SearchBar = (props) => {
  const { onSearch = () => null } = props;

  const { pathname, search } = useLocation();
  const history = useHistory();
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsCount, setSuggestionsCount] = useState(0);
  const [showNoResult, setShowNoResult] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [refList, setRefList] = useState({});
  const [searchExpanded, setSearchExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const { displayError } = useContext(MessageContext);
  const { language } = useContext(LanguageContext);

  const goToSearchPage = () => {
    if (value) {
      onSearch();
      history.push({
        pathname: `/rechercher`,
        search: `?${createQueryParams({ q: value })}`,
        state: { prevPath: pathname + search },
      });
    }
  };

  const handleIconClick = () => {
    goToSearchPage();
  };

  const getSuggestions = async (newValue) => {
    try {
      const sugg = await PageService.search({ query: newValue, lang: language, from: 0, size: 50 });
      const { hits } = sugg || {};
      const { hits: results } = hits || {};
      const suggestions = (results && Array.isArray(results) && results.map((res) => res._source).slice(0, 5)) || [];
      setSuggestions(suggestions);
      setSuggestionsCount(suggestions.length);
      setShowNoResult(suggestions.length === 0);
      setSearchExpanded(true);

      return suggestions;
    } catch (e) {
      displayError(
        "Le service de recherche est actuellement indisponible. Veuillez nous excuser pour la gêne occasionnée.",
      );
      setSuggestions([]);
      setSuggestionsCount(0);
      setShowNoResult(false);
      return [];
    }
  };

  const hasSuggestions = suggestions.length > 0;
  const hasResultsList = (hasSuggestions || showNoResult) && searchExpanded;
  const activeOptionId = hasSuggestions ? `search-input-option-${cursor}` : undefined;
  const suggestionsMessage = suggestionsCount
    ? `${suggestionsCount}${t[language].common.search.suggestions_message[suggestionsCount > 1 ? 1 : 0]}`
    : "";

  useEffect(() => {
    const refs = {};
    if (hasSuggestions) {
      suggestions.forEach((item, i) => {
        refs[i] = React.createRef();
      });
      setRefList(refs);
    }
  }, [hasSuggestions, suggestions]);

  const clearSuggestions = () => {
    setSuggestions([]);
    setSuggestionsCount(0);
    setShowNoResult(false);
  };

  const getSuggestionSubtitle = (suggestion) => {
    let breadcrumb = suggestion.breadcrumb.map((b) => b.title);
    if (breadcrumb && breadcrumb.length > 3) {
      breadcrumb = [breadcrumb[0], breadcrumb[1], "...", breadcrumb[breadcrumb.length - 1]];
    }

    return breadcrumb.join(` / `);
  };

  const getSuggestionLabel = (suggestion) => {
    const subtitle = getSuggestionSubtitle(suggestion);

    return subtitle ? `${suggestion.title}, ${subtitle}` : suggestion.title;
  };

  const renderSuggestion = (suggestion) => {
    const subtitle = getSuggestionSubtitle(suggestion);

    // Elastic gives us contents we can't parse
    const suggestionWithoutContent = suggestion;
    suggestionWithoutContent.contents = null;

    return (
      <Link page={suggestionWithoutContent} sx={{ color: "rgba(0, 0, 0, 0.87)", "&:hover": { color: "primary.main" } }}>
        <div className="fr-px-2w fr-py-3v" aria-label={getSuggestionLabel(suggestion)}>
          <b>{suggestion.title}</b>
          <p className="fr-text--xs">{subtitle}</p>
        </div>
      </Link>
    );
  };

  const handleChange = (event) => {
    const { value: newValue } = event.target;
    setValue(newValue);
    setCursor(0);
    if (newValue.trim().length <= 2) {
      clearSuggestions();
    } else {
      setSearchExpanded(true);
      debounce(getSuggestions, 300)(newValue);
    }
  };

  const handleSuggestionSelected = () => {
    onSearch();
    setValue("");
    clearSuggestions();
  };

  const handleKeyDown = (e) => {
    const ref = refList[cursor] && refList[cursor].current;
    if (e.keyCode === 38 /* Arrow-up */ && cursor > 0) {
      e.preventDefault();
      setCursor((prevState) => prevState - 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "end",
        inline: "nearest",
      });
    } else if (e.keyCode === 40 /* Arrow-down */ && cursor < suggestions.length - 1) {
      e.preventDefault();
      setCursor((prevState) => prevState + 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "start",
        inline: "nearest",
      });
    } else if (e.key === eventKey) {
      goToSearchPage();
      clearSuggestions();
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setSearchExpanded(false)}>
      <Box sx={containerStyled}>
        <Box
          className="fr-container fr-container-lg--fluid"
          sx={{
            display: { xs: "block", lg: "flex" },
            justifyContent: "flex-end",
            maxWidth: { lg: "22rem" },
            mr: { lg: 0 },
            "& div": {
              width: { lg: "22.25rem" },
            },
          }}
        >
          <Box
            className="fr-search-bar"
            id="search"
            role="search"
            sx={{ position: "absolute", left: { xs: "16px", lg: "auto" }, right: { xs: "16px", lg: "auto" } }}
          >
            <label className="fr-label" htmlFor="search-input" id="search-input-label">
              {t[language].common.search.label}
            </label>
            <input
              className="fr-input"
              aria-autocomplete="list"
              aria-controls={hasResultsList ? listboxId : undefined}
              aria-activedescendant={activeOptionId}
              aria-expanded={hasResultsList}
              type="search"
              autoComplete="off"
              id="search-input"
              name="search-input"
              role="combobox"
              title={t[language].common.search.input_title}
              placeholder={t[language].common.search.placeholder}
              value={value}
              onChange={handleChange}
              onFocus={() => setSearchExpanded(hasSuggestions || showNoResult)}
              onKeyDown={handleKeyDown}
            />
            <p className="fr-sr-only" aria-live="polite">
              {suggestionsMessage}
            </p>
            <button
              type="button"
              className="fr-btn"
              title={t[language].common.search.button_title}
              onClick={handleIconClick}
            >
              {t[language].common.search.placeholder}
            </button>
          </Box>
        </Box>
        {hasResultsList && (
          <Box
            id={listboxId}
            component="ul"
            role="listbox"
            aria-labelledby="search-input-label"
            sx={suggestionsListStyled(theme, isMobile)}
          >
            {hasSuggestions ? (
              suggestions.map((suggestion, i) => (
                <Box
                  id={`search-input-option-${i}`}
                  component="li"
                  role="option"
                  aria-selected={cursor === i}
                  aria-label={getSuggestionLabel(suggestion)}
                  key={`${suggestion.fullPath}-${suggestion.title}`}
                  ref={refList[i]}
                  sx={suggestionStyled(cursor === i)}
                  onClick={handleSuggestionSelected}
                >
                  {renderSuggestion(suggestion)}
                </Box>
              ))
            ) : (
              <Box component="li" role="option" aria-selected="false" className="fr-m-1w">
                <b>
                  {t[language].common.search.no_result_message} : &laquo; {value} &raquo;
                </b>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

SearchBar.propTypes = {
  onSearch: PropTypes.func,
};

export default SearchBar;
